import React, { useState } from "react";
import {
  IonContent,
  IonPage,
  IonButton,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonLoading,
  IonAlert,
} from "@ionic/react";
import { useHistory } from "react-router-dom";
import api from "@/services/api";
import { normalizeLanguage, setStoredLanguage, useI18n } from "@/i18n";

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    preferredLanguage: "de",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const history = useHistory();
  const { t } = useI18n();

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "preferredLanguage") {
      setStoredLanguage(normalizeLanguage(value));
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (formData.password !== formData.confirmPassword) {
      setError(t("passwordsDoNotMatch"));
      setLoading(false);
      return;
    }

    try {
      await api.post("/api/auth/register", {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        preferredLanguage: formData.preferredLanguage,
      });
      history.push("/login");
    } catch (err: any) {
      setError(err.response?.data?.error || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { marginBottom: "16px", "--border-radius": "8px" } as React.CSSProperties;

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="hero-section" style={{ padding: "32px 24px 24px" }}>
          <h1>{t("appTitle")}</h1>
        </div>

        <div className="auth-form">
          <h2>{t("registerCardTitle")}</h2>
          <form onSubmit={handleRegister}>
            <IonInput
              type="text"
              label={t("registerUsernameLabel")}
              labelPlacement="stacked"
              fill="outline"
              value={formData.username}
              onIonInput={(e) => handleInputChange("username", e.detail.value!)}
              required
              style={inputStyle}
            />
            <IonInput
              type="email"
              label={t("registerEmailLabel")}
              labelPlacement="stacked"
              fill="outline"
              value={formData.email}
              onIonInput={(e) => handleInputChange("email", e.detail.value!)}
              required
              style={inputStyle}
            />
            <IonInput
              type="password"
              label={t("registerPasswordLabel")}
              labelPlacement="stacked"
              fill="outline"
              value={formData.password}
              onIonInput={(e) => handleInputChange("password", e.detail.value!)}
              required
              style={inputStyle}
            />
            <IonInput
              type="password"
              label={t("registerConfirmPasswordLabel")}
              labelPlacement="stacked"
              fill="outline"
              value={formData.confirmPassword}
              onIonInput={(e) => handleInputChange("confirmPassword", e.detail.value!)}
              required
              style={inputStyle}
            />

            <div style={{ marginBottom: "24px" }}>
              <IonSelect
                label={t("preferredLanguage")}
                labelPlacement="stacked"
                fill="outline"
                value={formData.preferredLanguage}
                onIonChange={(e) => handleInputChange("preferredLanguage", e.detail.value)}
                style={{ "--border-radius": "8px" } as React.CSSProperties}
              >
                <IonSelectOption value="en">{t("english")}</IonSelectOption>
                <IonSelectOption value="de">{t("german")}</IonSelectOption>
              </IonSelect>
            </div>

            <IonButton
              expand="block"
              type="submit"
              disabled={loading}
              className="save-btn"
              style={{ "--border-radius": "10px" }}
            >
              {loading ? t("registerLoading") : t("registerButton")}
            </IonButton>
          </form>

          <IonButton
            expand="block"
            fill="clear"
            size="small"
            routerLink="/login"
            routerDirection="back"
            style={{ marginTop: "12px" }}
          >
            {t("registerHaveAccount")}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            size="small"
            routerLink="/how-it-works"
            routerDirection="back"
          >
            {t("howItWorksNav")}
          </IonButton>
        </div>

        <IonLoading isOpen={loading} message={t("registerLoading")} />
        <IonAlert
          isOpen={!!error}
          onDidDismiss={() => setError("")}
          header={t("errorTitle")}
          message={error}
          buttons={[t("okButton")]}
        />
      </IonContent>
    </IonPage>
  );
};

export default Register;
